import { Box,Drawer,List,ListItemButton,ListItemIcon,ListItemText,Typography,Tooltip } from "@mui/material";
import { useLocation,useNavigate } from "react-router-dom";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import logo from "../../../static/logo.webp";
import { useAppTheme } from "../../../context/ThemeContext";

export default function Sidebar({expanded,onMobileClose,mobileOpen=false,expandedWidth=260,collapsedWidth=76}){
  const navigate=useNavigate();
  const location=useLocation();
  const {colors,isDark}=useAppTheme();

  const width=expanded?expandedWidth:collapsedWidth;

  const isHome=location.pathname==="/delegado";
  const isPlayers=location.pathname.startsWith("/delegado/jugadores");
  const isTournaments=location.pathname.startsWith("/delegado/torneos");
  const isMatches=location.pathname.startsWith("/delegado/partidos");

  const handleNavigate=path=>{
    navigate(path);
    if(onMobileClose) onMobileClose();
  };

  const activeColor=isDark?colors.text:colors.primary;

  const mainItemSx=(active=false)=>({
    borderRadius:"8px",
    mb:0.25,
    minHeight:38,
    px:expanded?1.2:0,
    justifyContent:expanded?"flex-start":"center",
    color:active?activeColor:colors.tableHeaderText,
    backgroundColor:active?colors.surface:"transparent",
    "& .MuiListItemIcon-root":{
      color:active?activeColor:colors.tableHeaderText
    },
    "& .MuiListItemText-primary":{
      color:active?activeColor:colors.tableHeaderText
    },
    "& .MuiSvgIcon-root":{
      color:active?activeColor:colors.tableHeaderText
    },
    "&:hover":{
      backgroundColor:colors.surface,
      color:activeColor,
      "& .MuiListItemIcon-root":{
        color:activeColor
      },
      "& .MuiListItemText-primary":{
        color:activeColor
      },
      "& .MuiSvgIcon-root":{
        color:activeColor
      },
    },
  });

  const mainIconSx={
    minWidth:expanded?34:0,
    justifyContent:"center",
  };

  const mainTextProps={
    fontSize:"0.84rem",
    fontWeight:600,
  };

  const content=(
    <Box sx={{height:"100%",display:"flex",flexDirection:"column",backgroundColor:colors.primary,overflow:"hidden"}}>
      <Box
        onClick={()=>handleNavigate("/delegado")}
        sx={{
          height:72,
          px:expanded?2:1,
          display:"flex",
          alignItems:"center",
          justifyContent:expanded?"flex-start":"center",
          borderBottom:`1px solid ${colors.border}`,
          cursor:"pointer",
          flexShrink:0,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            maxWidth:expanded?150:44,
            width:"auto",
            height:expanded?46:40,
            objectFit:"contain",
          }}
        />
      </Box>

      <Box
        sx={{
          flex:1,
          overflowY:"auto",
          overflowX:"hidden",
          px:0.8,
          py:1,
          "&::-webkit-scrollbar":{
            width:5
          },
          "&::-webkit-scrollbar-thumb":{
            backgroundColor:colors.borderDark,
            borderRadius:"10px",
          },
        }}
      >
        {expanded&&(
          <Typography
            sx={{
              px:1.2,
              mb:0.7,
              fontSize:"0.68rem",
              fontWeight:600,
              letterSpacing:"0.06em",
              color:colors.tableHeaderText,
              opacity:0.7,
            }}
          >
            ADMINISTRACIÓN
          </Typography>
        )}

        <List disablePadding>
          <Tooltip title={expanded?"":"Inicio"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/delegado")}
              sx={mainItemSx(isHome)}
            >
              <ListItemIcon sx={mainIconSx}>
                <HomeRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Inicio"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>

          <Tooltip title={expanded?"":"Jugadores"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/delegado/jugadores")}
              sx={mainItemSx(isPlayers)}
            >
              <ListItemIcon sx={mainIconSx}>
                <BadgeRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Jugadores"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>

          <Tooltip title={expanded?"":"Torneos"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/delegado/torneos")}
              sx={mainItemSx(isTournaments)}
            >
              <ListItemIcon sx={mainIconSx}>
                <EmojiEventsRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Torneos"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>

          <Tooltip title={expanded?"":"Partidos"} placement="right">
            <ListItemButton
              onClick={()=>handleNavigate("/delegado/partidos")}
              sx={mainItemSx(isMatches)}
            >
              <ListItemIcon sx={mainIconSx}>
                <CalendarMonthRoundedIcon sx={{fontSize:20}} />
              </ListItemIcon>

              {expanded&&(
                <ListItemText
                  primary="Partidos"
                  primaryTypographyProps={mainTextProps}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>

      <Box sx={{p:0.8,borderTop:`1px solid ${colors.border}`}}>
        <Tooltip title={expanded?"":"Ir al sitio web"} placement="right">
          <ListItemButton
            onClick={()=>handleNavigate("/")}
            sx={mainItemSx(false)}
          >
            <ListItemIcon sx={mainIconSx}>
              <LanguageRoundedIcon sx={{fontSize:20}} />
            </ListItemIcon>

            {expanded&&(
              <ListItemText
                primary="Ir al sitio web"
                primaryTypographyProps={mainTextProps}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        open
        sx={{
          display:{xs:"none",md:"block"},
          width,
          flexShrink:0,
          "& .MuiDrawer-paper":{
            width,
            boxSizing:"border-box",
            border:0,
            transition:"width .22s ease",
            overflow:"hidden",
          },
        }}
      >
        {content}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{keepMounted:true}}
        sx={{
          display:{xs:"block",md:"none"},
          "& .MuiDrawer-paper":{
            width:expandedWidth,
            boxSizing:"border-box",
            border:0,
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
}